import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Search,
  Trash2,
  Edit3,
  ChevronLeft,
  ChevronRight,
  Loader2,
  CheckCircle,
  Sparkles,
  X,
  Layers,
  AlertCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../lib/api';
import Modal from '../components/ui/Modal';
import { stripUnderline } from '../lib/richText';
import { getErrorMessage } from '../lib/errors';
import { QuestionTypePreset } from '../lib/questionPresets';
import { resolveContentBlocks } from '@eps/shared-utils';
import {
  type EditableQuestion,
  type QuestionForm,
  CATEGORIES,
  SUBCATEGORY_LABELS,
  DIFFICULTIES,
  QUESTION_LENGTH_TYPES,
  TIERS,
  genGroupId,
  emptyForm,
  nextForm,
  blocksValidationError,
  toPayload,
} from '../lib/questionForm';
import QuestionFormFields from '../components/questions/QuestionFormFields';

function CreateQuestionModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<QuestionForm>(emptyForm());
  const stemError = blocksValidationError(form);

  const mutation = useMutation({
    mutationFn: async (data: QuestionForm) => {
      await api.post('/questions', toPayload(data));
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['questions'] });
      queryClient.invalidateQueries({ queryKey: ['question-groups'] });
      toast.success('Question created');
      setForm(nextForm(variables));
      onClose();
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed to create question')),
  });

  return (
    <Modal open={open} onClose={onClose} title="Create Question" xwide>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (stemError) return;
          mutation.mutate(form);
        }}
        className="space-y-4"
      >
        <QuestionFormFields form={form} setForm={setForm} />
        {stemError && (
          <p className="text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            {stemError}
          </p>
        )}
        <div className="flex items-center gap-3 pt-2">
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={form.publishImmediately}
              onChange={(e) => setForm({ ...form, publishImmediately: e.target.checked })}
              className="rounded border-gray-300"
            />
            Publish immediately
          </label>
          <div className="flex-1" />
          <button type="button" onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button
            type="submit"
            disabled={mutation.isPending || stemError !== null}
            className="btn-primary flex items-center gap-2"
          >
            {mutation.isPending ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Plus size={16} />
            )}
            {form.publishImmediately ? 'Create & Publish' : 'Create as Draft'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function EditQuestionModal({
  open,
  onClose,
  question,
}: {
  open: boolean;
  onClose: () => void;
  question: EditableQuestion;
}) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<QuestionForm>(() => ({
    name: question.name || '',
    types: question.types || ['ubt'],
    category: question.category || 'reading',
    subcategory: question.subcategory || '',
    difficulty: question.difficulty || 'beginner',
    questionLengthType: question.questionLengthType || 'short',
    tier: question.tier || 'free',
    groupId: question.groupId || '',
    ubtLayout: question.ubtLayout || 'set_with_stem',
    ubtPrompt: {
      stem: question.ubtPrompt?.stem || '',
    },
    question: {
      format: question.question?.format || 'text',
      text: question.question?.text || '',
      imageUrl: question.question?.imageUrl || '',
      audioUrl: question.question?.audioUrl || '',
      blocks: resolveContentBlocks({
        blocks: question.question?.blocks,
        text: question.question?.text || '',
        imageUrl: question.question?.imageUrl || '',
        audioUrl: question.question?.audioUrl || '',
      }),
    },
    options: (question.options || []).map((o) => ({
      format: o.format || 'text',
      text: o.text || '',
      imageUrl: o.imageUrl || '',
      audioUrl: o.audioUrl || '',
      isCorrect: o.isCorrect || false,
    })),
    explanation: { en: question.explanation?.en || '' },
    tags: (question.tags || []).join(', '),
    publishImmediately: false,
    questionType: (question.questionType as QuestionTypePreset | undefined) ?? null,
  }));
  const stemError = blocksValidationError(form);

  const mutation = useMutation({
    mutationFn: async (data: QuestionForm) => {
      await api.put(`/questions/${question._id}`, toPayload(data));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['questions'] });
      queryClient.invalidateQueries({ queryKey: ['question-groups'] });
      toast.success('Question updated');
      onClose();
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed to update question')),
  });

  return (
    <Modal open={open} onClose={onClose} title="Edit Question" xwide>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (stemError) return;
          mutation.mutate(form);
        }}
        className="space-y-4"
      >
        <QuestionFormFields form={form} setForm={setForm} />
        {stemError && (
          <p className="text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            {stemError}
          </p>
        )}
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button
            type="submit"
            disabled={mutation.isPending || stemError !== null}
            className="btn-primary flex items-center gap-2"
          >
            {mutation.isPending ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Edit3 size={16} />
            )}
            Save
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ── EPS Section Parser ─────────────────────────────────────────────────────
interface ParsedEpsQuestion {
  qNum: number;
  text: string;
  options: string[]; // length 4
  correctIndex: number | null;
  groupId?: string;
  groupPrompt?: string;
  layout: 'set_with_stem' | 'number_only';
}

function parseEpsSection(raw: string): { questions: ParsedEpsQuestion[]; warnings: string[] } {
  const warnings: string[] = [];
  const lines = raw
    .replace(/\r/g, '')
    .replace(/\u00a0/g, ' ')
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);

  // Pre-split single-line multi-option lines like "1. 폭발 √2.추락  3.감전  4. 금연"
  const expanded: string[] = [];
  for (const line of lines) {
    const optionMarkerRegex = /(?:^|\s)([1-4])[.)]/g;
    const matches = [...line.matchAll(optionMarkerRegex)];
    if (matches.length >= 3) {
      const splits: number[] = [];
      for (const m of matches) {
        const idx = m.index !== undefined ? (m[0].startsWith(' ') ? m.index + 1 : m.index) : -1;
        if (idx >= 0) splits.push(idx);
      }
      splits.push(line.length);
      for (let i = 0; i < splits.length - 1; i++) {
        const chunk = line.slice(splits[i], splits[i + 1]).trim();
        if (chunk) expanded.push(chunk);
      }
    } else {
      expanded.push(line);
    }
  }

  const groupHeaderRegex = /^\[\s*(\d+)\s*[-–]\s*(\d+)\s*\.?\]\s*(.*)$/;
  const numberHeadRegex = /^(\d+)[.)：:]\s*(.*)$/;
  const circledOptRegex = /^([①②③④])\s*(.*)$/;
  const circledIndex: Record<string, number> = { '①': 0, '②': 1, '③': 2, '④': 3 };

  const questions: ParsedEpsQuestion[] = [];
  let currentGroup: { id: string; prompt: string; start: number; end: number } | null = null;
  let collectingGroupPrompt = false;
  let current: ParsedEpsQuestion | null = null;
  const flush = () => {
    if (!current) return;
    const filledCount = current.options.filter((o) => o.length > 0).length;
    if (filledCount < 2) {
      warnings.push(`Q${current.qNum}: only ${filledCount} option(s) detected — skipped`);
    } else {
      if (current.options.some((o) => !o)) {
        warnings.push(`Q${current.qNum}: missing options padded as empty`);
      }
      if (current.correctIndex === null) {
        warnings.push(`Q${current.qNum}: no √ marker — set correct answer manually`);
      }
      questions.push(current);
    }
    current = null;
  };

  for (const line of expanded) {
    const gm = line.match(groupHeaderRegex);
    if (gm) {
      flush();
      currentGroup = {
        id: genGroupId(),
        prompt: gm[3].trim(),
        start: parseInt(gm[1], 10),
        end: parseInt(gm[2], 10),
      };
      collectingGroupPrompt = !currentGroup.prompt;
      continue;
    }

    // Option line in current question (1-4 or ①-④)
    if (current && current.options.filter((o) => o).length < 4) {
      const cm = line.match(circledOptRegex);
      const nm = line.match(numberHeadRegex);
      if (cm) {
        const idx = circledIndex[cm[1]];
        const txt = cm[2].replace(/√/g, '').trim();
        current.options[idx] = txt;
        if (cm[2].includes('√')) current.correctIndex = idx;
        if (current.options.every((o) => o)) flush();
        continue;
      }
      if (nm) {
        const n = parseInt(nm[1], 10);
        if (n >= 1 && n <= 4) {
          const idx = n - 1;
          const txt = nm[2].replace(/√/g, '').trim();
          current.options[idx] = txt;
          if (nm[2].includes('√')) current.correctIndex = idx;
          if (current.options.every((o) => o)) flush();
          continue;
        }
      }
      // Continuation of question text (before any option)
      if (current.options.every((o) => !o)) {
        current.text = (current.text + ' ' + line).trim();
        continue;
      }
    }

    // New question header
    const nm = line.match(numberHeadRegex);
    if (nm) {
      const n = parseInt(nm[1], 10);
      if (n >= 1 && n <= 99) {
        flush();
        const text = nm[2].trim();
        current = {
          qNum: n,
          text,
          options: ['', '', '', ''],
          correctIndex: null,
          ...(currentGroup ? { groupId: currentGroup.id, groupPrompt: currentGroup.prompt } : {}),
          layout: text ? 'set_with_stem' : 'number_only',
        };
        collectingGroupPrompt = false;
        continue;
      }
    }

    // Otherwise, if collecting group prompt, append
    if (currentGroup && collectingGroupPrompt) {
      currentGroup.prompt = (currentGroup.prompt + ' ' + line).trim();
      continue;
    }
  }
  flush();

  // Backfill groupPrompt onto questions whose group prompt was set later
  for (const q of questions) {
    if (q.groupId && !q.groupPrompt) {
      // find another q in same group with prompt
      const sibling = questions.find((s) => s.groupId === q.groupId && s.groupPrompt);
      if (sibling) q.groupPrompt = sibling.groupPrompt;
    }
  }

  return { questions, warnings };
}

// ── Bulk Import Modal ──────────────────────────────────────────────────────
interface BulkDefaults {
  category: string;
  subcategory: string;
  difficulty: string;
  tier: string;
  questionLengthType: string;
  types: string[];
  publishImmediately: boolean;
}

function BulkImportModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [raw, setRaw] = useState('');
  const [defaults, setDefaults] = useState<BulkDefaults>({
    category: 'reading',
    subcategory: '',
    difficulty: 'beginner',
    tier: 'free',
    questionLengthType: 'short',
    types: ['ubt'],
    publishImmediately: false,
  });
  const [parsed, setParsed] = useState<ParsedEpsQuestion[] | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);

  const handleParse = () => {
    if (!raw.trim()) {
      toast.error('Paste some content first');
      return;
    }
    const { questions, warnings } = parseEpsSection(raw);
    if (questions.length === 0) {
      toast.error('No questions detected. Check format.');
      setParsed(null);
      setWarnings(warnings);
      return;
    }
    setParsed(questions);
    setWarnings(warnings);
    toast.success(`Parsed ${questions.length} question(s)`);
  };

  const updateQ = (idx: number, patch: Partial<ParsedEpsQuestion>) => {
    if (!parsed) return;
    const next = [...parsed];
    next[idx] = { ...next[idx], ...patch };
    setParsed(next);
  };

  const updateQOption = (qIdx: number, oIdx: number, text: string) => {
    if (!parsed) return;
    const next = [...parsed];
    const opts = [...next[qIdx].options];
    opts[oIdx] = text;
    next[qIdx] = { ...next[qIdx], options: opts };
    setParsed(next);
  };

  const removeQ = (idx: number) => {
    if (!parsed) return;
    setParsed(parsed.filter((_, i) => i !== idx));
  };

  const mutation = useMutation({
    mutationFn: async () => {
      if (!parsed) return;
      const ready = parsed.filter((q) => q.correctIndex !== null && q.options.every((o) => o));
      if (ready.length === 0)
        throw new Error('All questions need 4 filled options and a correct answer marked');
      const payloads = ready.map((q) => ({
        ...(defaults.subcategory && { subcategory: defaults.subcategory }),
        types: defaults.types,
        category: defaults.category,
        difficulty: defaults.difficulty,
        questionLengthType: defaults.questionLengthType,
        tier: defaults.tier,
        ...(q.groupId && { groupId: q.groupId }),
        ...(defaults.types.includes('ubt') && {
          ubtLayout: q.layout,
          ubtPrompt: { ...(q.groupPrompt && { stem: q.groupPrompt }) },
        }),
        question: { format: 'text', ...(q.text && { text: q.text }) },
        options: q.options.map((text, i) => ({
          format: 'text',
          text,
          isCorrect: i === q.correctIndex,
        })),
        ...(defaults.publishImmediately && { publishStatus: 'published' }),
      }));
      await api.post('/questions/bulk', { questions: payloads });
      return ready.length;
    },
    onSuccess: (count) => {
      queryClient.invalidateQueries({ queryKey: ['questions'] });
      queryClient.invalidateQueries({ queryKey: ['question-groups'] });
      toast.success(`Created ${count} question(s)`);
      setRaw('');
      setParsed(null);
      setWarnings([]);
      onClose();
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Bulk create failed')),
  });

  return (
    <Modal open={open} onClose={onClose} title="Bulk Import EPS Questions" wide>
      <div className="space-y-4">
        <div className="rounded-xl border border-blue-200 bg-blue-50/40 p-4">
          <p className="text-sm text-gray-700 mb-2">
            Paste an EPS-format section. Recognized patterns:
          </p>
          <ul className="text-xs text-gray-600 list-disc list-inside space-y-0.5">
            <li>
              <code>[1-4] 그림을 보고 맞는 것을 고르십시오.</code> — group header (questions 1-4
              share this prompt)
            </li>
            <li>
              <code>5. 이 사람이 일하는 것은 어디입니까?</code> — question with its own stem
            </li>
            <li>
              <code>4.</code> on its own line — number-only question (uses group prompt)
            </li>
            <li>
              <code>1. 옵션 √</code> — option with √ marking the correct answer
            </li>
            <li>
              Both <code>1./2./3./4.</code> and <code>①②③④</code> option markers are accepted
            </li>
          </ul>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Category</label>
            <select
              value={defaults.category}
              onChange={(e) =>
                setDefaults({ ...defaults, category: e.target.value, subcategory: '' })
              }
              className="input-field"
            >
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Difficulty</label>
            <select
              value={defaults.difficulty}
              onChange={(e) => setDefaults({ ...defaults, difficulty: e.target.value })}
              className="input-field"
            >
              {DIFFICULTIES.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Tier</label>
            <select
              value={defaults.tier}
              onChange={(e) => setDefaults({ ...defaults, tier: e.target.value })}
              className="input-field"
            >
              {TIERS.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Length</label>
            <select
              value={defaults.questionLengthType}
              onChange={(e) => setDefaults({ ...defaults, questionLengthType: e.target.value })}
              className="input-field"
            >
              {QUESTION_LENGTH_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={defaults.publishImmediately}
                onChange={(e) => setDefaults({ ...defaults, publishImmediately: e.target.checked })}
                className="rounded border-gray-300"
              />
              Publish immediately
            </label>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Paste EPS section</label>
          <textarea
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            className="input-field font-korean"
            rows={10}
            placeholder={`[1-4] 그림을 보고 맞는 것을 고르십시오.\n1. 물을 마시고 있습니다.√\n2. 술을 마시고 있습니다.\n3. 주스를 마시고 있습니다.\n4. 커피를 마시고 있습니다`}
          />
          <div className="flex gap-2 mt-2">
            <button
              type="button"
              onClick={handleParse}
              className="btn-secondary text-sm flex items-center gap-1.5"
            >
              <Sparkles size={14} /> Parse
            </button>
            <button
              type="button"
              onClick={() => {
                setRaw('');
                setParsed(null);
                setWarnings([]);
              }}
              className="btn-secondary text-sm"
            >
              Clear
            </button>
          </div>
        </div>

        {warnings.length > 0 && (
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
            <p className="text-xs font-semibold text-amber-800 flex items-center gap-1 mb-1">
              <AlertCircle size={12} /> Warnings
            </p>
            <ul className="text-xs text-amber-700 list-disc list-inside space-y-0.5">
              {warnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          </div>
        )}

        {parsed && parsed.length > 0 && (
          <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
            <p className="text-sm font-semibold text-gray-700">{parsed.length} question(s) ready</p>
            {parsed.map((q, qi) => {
              const ready = q.correctIndex !== null && q.options.every((o) => o);
              return (
                <div
                  key={qi}
                  className={`rounded-lg border p-3 space-y-2 ${ready ? 'border-success-200 bg-success-50/30' : 'border-amber-200 bg-amber-50/30'}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-gray-500">Q{q.qNum}</span>
                    {q.groupId && (
                      <span className="badge bg-blue-50 text-blue-700 text-[10px]">
                        group {q.groupId.slice(0, 8)}
                      </span>
                    )}
                    <span className="badge bg-gray-100 text-gray-600 text-[10px]">{q.layout}</span>
                    <div className="flex-1" />
                    <button
                      type="button"
                      onClick={() => removeQ(qi)}
                      className="text-gray-400 hover:text-danger-500"
                    >
                      <X size={14} />
                    </button>
                  </div>
                  {q.groupPrompt && (
                    <input
                      type="text"
                      value={q.groupPrompt}
                      onChange={(e) => updateQ(qi, { groupPrompt: e.target.value })}
                      className="input-field text-xs font-korean italic"
                      placeholder="Group prompt"
                    />
                  )}
                  <input
                    type="text"
                    value={q.text}
                    onChange={(e) =>
                      updateQ(qi, {
                        text: e.target.value,
                        layout: e.target.value ? 'set_with_stem' : 'number_only',
                      })
                    }
                    className="input-field text-sm font-korean"
                    placeholder={
                      q.layout === 'number_only'
                        ? '(number-only — uses group prompt)'
                        : 'Question text'
                    }
                  />
                  <div className="grid grid-cols-2 gap-1.5">
                    {q.options.map((opt, oi) => (
                      <div key={oi} className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => updateQ(qi, { correctIndex: oi })}
                          className={`shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${q.correctIndex === oi ? 'border-success-500 bg-success-50 text-success-600' : 'border-gray-300 text-transparent hover:border-gray-400'}`}
                          title="Mark correct"
                        >
                          <CheckCircle size={11} />
                        </button>
                        <span className="text-xs text-gray-400">{'①②③④'[oi]}</span>
                        <input
                          type="text"
                          value={opt}
                          onChange={(e) => updateQOption(qi, oi, e.target.value)}
                          className="input-field text-xs font-korean !py-1"
                          placeholder={`Option ${oi + 1}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
          <button type="button" onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button
            type="button"
            disabled={!parsed || parsed.length === 0 || mutation.isPending}
            onClick={() => mutation.mutate()}
            className="btn-primary flex items-center gap-2"
          >
            {mutation.isPending ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Plus size={16} />
            )}
            Create{' '}
            {parsed?.filter((q) => q.correctIndex !== null && q.options.every((o) => o)).length ??
              0}{' '}
            question(s)
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default function QuestionsPage() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [questionLengthType, setQuestionLengthType] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [editQuestion, setEditQuestion] = useState<EditableQuestion | null>(null);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['questions', page, search, questionLengthType],
    queryFn: async () => {
      const res = await api.get('/questions', {
        params: {
          page,
          limit: 20,
          search,
          ...(questionLengthType !== 'all' && { questionLengthType }),
        },
      });
      return res.data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/questions/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['questions'] });
      toast.success('Question deleted');
    },
    onError: () => toast.error('Failed to delete'),
  });

  const publishMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.post(`/questions/${id}/publish`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['questions'] });
      toast.success('Question published');
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed to publish')),
  });

  const questions = data?.data ?? [];
  const pagination = data?.pagination ?? { total: 0, page: 1, totalPages: 1 };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Questions</h1>
          <p className="text-gray-500 mt-1">{pagination.total} total questions</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowBulk(true)}
            className="btn-secondary flex items-center gap-2"
          >
            <Layers size={16} /> Bulk Import
          </button>
          <button
            onClick={() => setShowCreate(true)}
            className="btn-primary flex items-center gap-2"
          >
            <Plus size={16} /> Add Question
          </button>
        </div>
      </div>

      <CreateQuestionModal open={showCreate} onClose={() => setShowCreate(false)} />
      <BulkImportModal open={showBulk} onClose={() => setShowBulk(false)} />
      {editQuestion && (
        <EditQuestionModal
          open={!!editQuestion}
          onClose={() => setEditQuestion(null)}
          question={editQuestion}
        />
      )}

      <div className="card">
        <div className="flex items-center gap-3 mb-4">
          <div className="relative flex-1 max-w-sm">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search questions..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="input-field pl-9"
            />
          </div>
          <select
            value={questionLengthType}
            onChange={(e) => {
              setQuestionLengthType(e.target.value);
              setPage(1);
            }}
            className="input-field max-w-[180px]"
          >
            <option value="all">All lengths</option>
            {QUESTION_LENGTH_TYPES.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 font-medium text-gray-500">Question</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500">Type</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500">Difficulty</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500">Length</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500">Category</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500">Status</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-gray-400">
                    Loading...
                  </td>
                </tr>
              ) : questions.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-gray-400">
                    No questions found
                  </td>
                </tr>
              ) : (
                questions.map((q: EditableQuestion) => (
                  <tr key={q._id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4">
                      {(() => {
                        const promptLabel = q.ubtPrompt?.stem
                          ? stripUnderline(q.ubtPrompt.stem)
                          : '';
                        const questionText = stripUnderline(q.question?.text);
                        const primaryLabel =
                          q.name ||
                          promptLabel ||
                          questionText ||
                          q.question?.imageUrl ||
                          q.question?.audioUrl ||
                          'Untitled';
                        const secondaryLabel = q.name
                          ? promptLabel ||
                            questionText ||
                            q.question?.imageUrl ||
                            q.question?.audioUrl ||
                            'No question content preview'
                          : questionText || q.question?.imageUrl || q.question?.audioUrl || '';
                        return (
                          <div className="space-y-0.5">
                            <span className="font-medium text-gray-900 line-clamp-1">
                              {primaryLabel}
                            </span>
                            {secondaryLabel ? (
                              <p className="text-xs text-gray-500 line-clamp-1">{secondaryLabel}</p>
                            ) : null}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="py-3 px-4">
                      <span className="badge bg-blue-50 text-blue-700">
                        {(q.types || []).join(', ')}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className="badge bg-yellow-50 text-yellow-700">{q.difficulty}</span>
                    </td>
                    <td className="py-3 px-4">
                      <span className="badge bg-purple-50 text-purple-700">
                        {q.questionLengthType || '—'}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-gray-600">
                      {q.subcategory
                        ? `${q.category} / ${SUBCATEGORY_LABELS[q.subcategory] ?? q.subcategory}`
                        : q.category}
                    </td>
                    <td className="py-3 px-4">
                      <span
                        className={`badge ${q.publishStatus === 'published' ? 'bg-success-50 text-success-700' : 'bg-gray-100 text-gray-600'}`}
                      >
                        {q.publishStatus}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-1">
                        {q.publishStatus !== 'published' && (
                          <button
                            onClick={() => publishMutation.mutate(q._id)}
                            className="p-1 text-gray-400 hover:text-success-600"
                            title="Publish"
                          >
                            <CheckCircle size={14} />
                          </button>
                        )}
                        <button
                          onClick={() => setEditQuestion(q)}
                          className="p-1 text-gray-400 hover:text-primary-600"
                          title="Edit"
                        >
                          <Edit3 size={14} />
                        </button>
                        <button
                          onClick={() => deleteMutation.mutate(q._id)}
                          className="p-1 text-gray-400 hover:text-danger-500"
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
          <p className="text-sm text-gray-500">
            Page {pagination.page} of {pagination.totalPages}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="btn-secondary text-xs flex items-center gap-1"
            >
              <ChevronLeft size={14} /> Prev
            </button>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={page >= pagination.totalPages}
              className="btn-secondary text-xs flex items-center gap-1"
            >
              Next <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
